import { useCallback, useEffect, useMemo, useState } from "react";
import {
    ActiveResponse,
    disableActive,
    editActive,
    enableActive,
    getActivesPaginated,
    newActive,
} from "../../../services/decisionFlow";
import { initialPagination, PaginatedResponse } from "../../../domain/models/paginated-response.model";

export type StatusFilter = "all" | "enabled" | "disabled";

type State = {
    open: boolean;
    active?: ActiveResponse;
};

/** Estado, fetch e handlers da administração de classes de ativos. */
export function useManageActives() {
    const [state, setState] = useState<State>({ open: false });
    const [actives, setActives] = useState<PaginatedResponse<ActiveResponse>>(initialPagination);
    const [modalName, setModalName] = useState("");
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

    const filteredRows = useMemo(() => {
        return actives.data.filter((row) => {
            const matchesSearch = !search || row.name.toLowerCase().includes(search.toLowerCase());
            const matchesStatus =
                statusFilter === "all" || (statusFilter === "enabled" && row.enabled) || (statusFilter === "disabled" && !row.enabled);
            return matchesSearch && matchesStatus;
        });
    }, [actives.data, search, statusFilter]);

    const fetchActives = useCallback(async (newPage: number = 0, newSize: number = 10) => {
        const result = await getActivesPaginated(newPage + 1, newSize);
        const response = result.data as PaginatedResponse<ActiveResponse>;
        setActives(response);
    }, []);

    useEffect(() => {
        fetchActives(0, 10);
    }, [fetchActives]);

    const handlePageChange = useCallback(
        async (newPage: number) => {
            await fetchActives(newPage - 1, actives.pagination.perPage);
        },
        [fetchActives, actives.pagination.perPage]
    );

    const handlePageSizeChange = useCallback(
        async (newSize: number) => {
            await fetchActives(0, newSize);
        },
        [fetchActives]
    );

    const handleEnableActive = useCallback(
        async (active: ActiveResponse) => {
            if (active.enabled) {
                await disableActive(active.id);
            } else {
                await enableActive(active.id);
            }
            await fetchActives();
        },
        [fetchActives]
    );

    const handleNewActive = useCallback(
        async (name: string, id?: number) => {
            if (id) {
                await editActive(id, name);
            } else {
                await newActive(name);
            }
            await fetchActives();
            setState({ open: false });
        },
        [fetchActives]
    );

    const openCreate = () => {
        setModalName("");
        setState({ open: true });
    };

    const openEdit = (active: ActiveResponse) => {
        setModalName(active.name);
        setState({ open: true, active: { ...active } });
    };

    const closeModal = () => {
        setState({ open: false });
    };

    const isEditing = Boolean(state.active);

    return {
        state,
        actives,
        modalName,
        setModalName,
        search,
        setSearch,
        statusFilter,
        setStatusFilter,
        filteredRows,
        isEditing,
        handlePageChange,
        handlePageSizeChange,
        handleEnableActive,
        handleNewActive,
        openCreate,
        openEdit,
        closeModal,
    };
}
